import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";

// Étoiles interactives (formulaire) ou en lecture seule (affichage)
const Stars = ({ value, onPick, size = 18, testid }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n} type="button" disabled={!onPick}
        onClick={() => onPick?.(n)}
        data-testid={onPick ? `${testid}-star-${n}` : undefined}
        className={onPick ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}
      >
        <Star size={size} className={n <= value ? "fill-[#d4af37] text-gold" : "text-ivory/25"} />
      </button>
    ))}
  </span>
);

export default function ProductReviews({ productId }) {
  const { user } = useAuth();
  const { t, ts } = useLang();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/products/${productId}/reviews`).then((r) => setData(r.data)).catch(() => setData({ reviews: [], count: 0, average: 0 }));
  useEffect(() => { load(); }, [productId, user?.id]);

  if (!data) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) return;
    setBusy(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, text: text.trim() });
      toast.success(t("productReviews.thanks"));
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-16 border-t border-ivory/10 pt-10" data-testid="product-reviews">
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <h2 className="font-serif text-3xl">{t("productReviews.title")}</h2>
        {data.count > 0 && (
          <span className="inline-flex items-center gap-2 text-sm text-ivory/70" data-testid="product-reviews-summary">
            <Stars value={Math.round(data.average)} testid="product-reviews-stars" />
            <strong className="text-gold">{String(data.average).replace(".", ",")}/5</strong>
            — {t("productReviews.based_on")} {data.count} {t("productReviews.reviews_word")}
          </span>
        )}
      </div>

      {data.count === 0 && (
        <p className="text-ivory/50 text-sm mb-8" data-testid="product-reviews-none">{t("productReviews.none")}</p>
      )}

      <ul className="space-y-5 mb-10">
        {(data.reviews || []).map((r) => (
          <li key={r.id} className="bg-surface border border-ivory/10 rounded-xl p-5" data-testid={`product-review-${r.id}`}>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <Stars value={r.rating} testid={`product-review-stars-${r.id}`} />
              <span className="text-sm font-semibold text-ivory/90">{r.name}</span>
              {r.verified && (
                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 border border-emerald-400/30 rounded-full px-2 py-0.5">
                  <BadgeCheck size={11} /> {t("productReviews.verified")}
                </span>
              )}
              <span className="text-[11px] text-ivory/40 ml-auto">
                {new Date(r.created_at).toLocaleDateString("fr-CA", { timeZone: "America/Montreal" })}
              </span>
            </div>
            <p className="text-sm text-ivory/75 leading-relaxed">{r.text}</p>
          </li>
        ))}
      </ul>

      {/* Dépôt d'un avis : clients connectés ayant acheté le produit, un avis chacun, modéré */}
      {data.can_review && (
        <form onSubmit={submit} className="bg-surface border border-gold/20 rounded-xl p-6 max-w-xl" data-testid="product-review-form">
          <p className="text-[11px] uppercase tracking-widest text-gold mb-3">{t("productReviews.write")}</p>
          <div className="mb-3">
            <Stars value={rating} onPick={setRating} size={24} testid="product-review-rating" />
          </div>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder={ts("productReviews.your_text")} rows={4} maxLength={1000}
            data-testid="product-review-text"
            className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none mb-3"
          />
          <p className="text-[11px] text-ivory/45 mb-4">{t("productReviews.moderation_note")}</p>
          <button type="submit" disabled={busy || text.trim().length < 10}
            data-testid="product-review-submit"
            className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-6 py-3 rounded-full text-sm hover:bg-champagne transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : null}
            {busy ? t("productReviews.sending") : t("productReviews.send")}
          </button>
        </form>
      )}
      {data.pending && (
        <p className="text-amber-400/90 text-sm" data-testid="product-review-pending">{t("productReviews.pending")}</p>
      )}
      {data.already && (
        <p className="text-ivory/55 text-sm" data-testid="product-review-already">{t("productReviews.already")}</p>
      )}
      {!user && !data.pending && !data.already && (
        <p className="text-ivory/55 text-sm" data-testid="product-review-login">
          <Link to="/connexion" className="text-gold hover:underline">{t("productReviews.login_first")}</Link>
          {" — "}{t("productReviews.only_buyers")}
        </p>
      )}
      {user && !data.can_review && !data.pending && !data.already && (
        <p className="text-ivory/45 text-xs" data-testid="product-review-only-buyers">{t("productReviews.only_buyers")}</p>
      )}
    </section>
  );
}
